import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  CornerDownLeft,
  Plus,
  Sparkles,
  Building2,
} from 'lucide-react';
import { NAV_ITEMS } from '../constants';
import { TENANTS } from '../theme/tenants';
import type { Tenant } from '../types';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onTenantSelect: (tenant: Tenant) => void;
  query: string;
}

type CommandType = 'nav' | 'action' | 'tenant';

interface CommandItem {
  id: string;
  label: string;
  category: string;
  icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
  type: CommandType;
  shortcut?: string[];
  metadata?: Tenant;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onNavigate, onTenantSelect, query }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // -- 1. Define Static Actions (AI / Smart Suggestions) --
  const STATIC_ACTIONS: CommandItem[] = [
    { id: 'act-new-project', label: 'Create new project', category: 'Suggested', icon: Plus, type: 'action', shortcut: ['C'] },
    { id: 'act-ai-report', label: 'Generate AI Report', category: 'Suggested', icon: Sparkles, type: 'action', shortcut: ['G'] },
  ];

  // -- 2. Build Flattened List based on Query --
  const filteredItems = useMemo(() => {
    const q = query.toLowerCase();

    // A. Navigation Items
    const navs: CommandItem[] = NAV_ITEMS.map(item => ({
      id: item.id,
      label: item.label,
      category: 'Navigation',
      icon: item.icon,
      type: 'nav' as CommandType,
    })).filter(item => item.label.toLowerCase().includes(q));

    // B. Tenant Switching (Only show if query matches or empty)
    const tenants: CommandItem[] = TENANTS.map(t => ({
      id: `tenant-${t.id}`,
      label: `Switch to ${t.name}`,
      category: 'Companies',
      icon: Building2,
      type: 'tenant' as CommandType,
      metadata: t
    })).filter(item => item.label.toLowerCase().includes(q));

    // C. Actions
    const actions = STATIC_ACTIONS.filter(item => item.label.toLowerCase().includes(q));

    // Combine & Group
    let results = [];
    
    // If empty query, show "Smart" mix
    if (!q) {
      results = [
        ...STATIC_ACTIONS,
        ...navs.slice(0, 5), // Recent-ish
        ...tenants.slice(0, 2)
      ];
    } else {
      results = [...actions, ...navs, ...tenants];
    }

    return results;
  }, [query]);

  // -- 3. Effects --

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    if (listRef.current) {
      const activeElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Key Handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
          break;
        case 'Enter': {
          e.preventDefault();
          const item = filteredItems[selectedIndex];
          if (item) handleSelect(item);
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex]);

  // -- 4. Action Handlers --
  const handleSelect = (item: CommandItem) => {
    if (item.type === 'nav') {
      onNavigate(item.id);
    } else if (item.type === 'tenant') {
      if (item.metadata) {
        onTenantSelect(item.metadata as Tenant);
      }
    } else {
      // Action triggered: item.label - placeholder for future implementation
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - click to close */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />

      {/* Dropdown - positioned below search bar in header */}
      <div className="
        fixed z-50 w-[500px] right-[280px] top-[58px]
        bg-card border border-border
        rounded-xl shadow-swm-lg
        overflow-hidden
        animate-in fade-in slide-in-from-top-2 duration-200
      ">

        {/* Results List */}
        <div
          ref={listRef}
          className="max-h-[400px] overflow-y-auto p-2"
        >
          {filteredItems.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No results found.</p>
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const isSelected = index === selectedIndex;
              // Check if we need a category header
              const showHeader = index === 0 || filteredItems[index - 1].category !== item.category;

              return (
                <React.Fragment key={item.id}>
                  {showHeader && (
                    <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                      {item.category}
                    </div>
                  )}
                  <button
                    onClick={() => handleSelect(item)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors group
                      ${isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        p-1.5 rounded-md transition-colors
                        ${isSelected ? 'bg-card shadow-sm' : 'bg-muted text-muted-foreground'}
                      `}>
                        <item.icon size={14} strokeWidth={1.5} />
                      </div>
                      <span className={isSelected ? 'font-medium' : ''}>{item.label}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {item.shortcut && (
                        <div className="flex gap-1">
                          {item.shortcut.map(key => (
                            <kbd key={key} className="inline-flex h-5 items-center justify-center min-w-[18px] rounded border border-border bg-card px-1 font-mono text-[10px] text-muted-foreground">
                              {key}
                            </kbd>
                          ))}
                        </div>
                      )}
                      {isSelected && (
                         <CornerDownLeft size={12} className="text-primary" />
                      )}
                    </div>
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default CommandPalette;